import { createGtmEvent } from "@solvari/common-fe/argus/thick-client";
import { objectToSnake } from "ts-case-convert";

type GtmProperties = {
  gtmAction:
    | "javascript"
    | "jumplink"
    | "mailto"
    | "navigation-external"
    | "navigation-internal"
    | "slider"
    | "submit"
    | "tel";
  gtmLocation:
    | "banner"
    | "breadcrumbs"
    | "comments"
    | "footer-top"
    | "footer"
    | "header-bottom"
    | "header-top"
    | "main"
    | "popup"
    | "sidebar-cta"
    | "sidebar-nav";
  gtmType: "button" | "link-block" | "link";
  gtmUrl: string;
};

type UiClickEventPayload = Readonly<{
  linkAction: GtmProperties["gtmAction"];
  linkLocation: GtmProperties["gtmLocation"] | "unknown";
  linkPath: string[];
  linkType: GtmProperties["gtmType"];
  linkUrl: GtmProperties["gtmUrl"] | null;
}>;

function getLinkGtmAction(link: HTMLAnchorElement): GtmProperties["gtmAction"] {
  if (link.dataset.gtmAction) {
    return link.dataset.gtmAction as GtmProperties["gtmAction"];
  }

  if (link.protocol === "tel:") {
    return "tel";
  }

  if (link.protocol === "mailto:") {
    return "mailto";
  }

  if (link.hostname !== window.location.hostname) {
    return "navigation-external";
  }

  if (link.pathname === window.location.pathname && !!link.hash) {
    return "jumplink";
  }

  return "navigation-internal";
}

function getGtmType(element: HTMLElement): GtmProperties["gtmType"] {
  if (element.dataset.gtmType) {
    return element.dataset.gtmType as GtmProperties["gtmType"];
  }
  if (
    element.classList.contains("btn") ||
    element.classList.contains("button") ||
    element.tagName === "BUTTON"
  ) {
    return "button";
  }
  return "link";
}

function getGtmLocation(
  element: HTMLElement,
): UiClickEventPayload["linkLocation"] {
  if (element.dataset.gtmLocation) {
    return element.dataset.gtmLocation as UiClickEventPayload["linkLocation"];
  }
  if (element.closest(".main")) {
    return "main";
  }
  if (element.closest(".menu--sidebar")) {
    return "sidebar-nav";
  }
  if (element.closest(".menu--desktop")) {
    return "header-bottom";
  }
  return "unknown";
}

function getLinkPath(
  element: HTMLElement,
  existingPath?: UiClickEventPayload["linkPath"],
): UiClickEventPayload["linkPath"] {
  const parent = element.parentElement?.parentElement
    ?.closest(".menu__item--parent")
    ?.querySelector<HTMLElement>(".menu__item--parent > .menu__item__label");

  const path = existingPath
    ? [element.innerText, ...existingPath]
    : [element.innerText];
  return !parent ? path : getLinkPath(parent, path);
}

function getLinkPayload(link: HTMLAnchorElement): UiClickEventPayload {
  return {
    linkAction: getLinkGtmAction(link),
    linkType: getGtmType(link),
    linkLocation: getGtmLocation(link),
    linkUrl: link.href,
    linkPath: getLinkPath(link),
  };
}

function getButtonPayload(button: HTMLButtonElement): UiClickEventPayload {
  const action = button.dataset.gtmAction as GtmProperties["gtmAction"] | null;

  if (button.type === "submit") {
    return {
      linkType: getGtmType(button),
      linkLocation: getGtmLocation(button),
      linkAction: action ?? "submit",
      linkUrl: button.closest<HTMLFormElement>("form[action]")?.action ?? null,
      linkPath: getLinkPath(button),
    };
  }

  return {
    linkType: getGtmType(button),
    linkLocation: getGtmLocation(button),
    linkAction: action ?? "javascript",
    linkUrl: null,
    linkPath: getLinkPath(button),
  };
}

function initGtmLinkTracking() {
  document.querySelectorAll("button").forEach((button) => {
    button.addEventListener("click", () => {
      void createGtmEvent("ui_click", objectToSnake(getButtonPayload(button)));
    });
  });

  document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((link) => {
    link.addEventListener("click", () => {
      void createGtmEvent("ui_click", objectToSnake(getLinkPayload(link)));
    });
  });
}

export { initGtmLinkTracking };
